/**
 * FullCalendar-backed appointment calendar for the manage appointments page.
 *
 * This component is responsible for presentation and calendar interactions only:
 * transforming API records into FullCalendar events, opening details popups,
 * exposing the rendered date range for data loading, and rendering month-cell
 * affordances such as create buttons and external-conflict indicators.
 *
 * Appointment mutations, availability checks, and dialog workflows live outside
 * this component so the calendar stays a view over already-loaded data.
 *
 * @module AppointmentsCalendar
 */

import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect, useContext } from 'react';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Card, Typography, DatePicker, Space, theme, Spin } from 'antd';
import { isSameDay, parseISO } from 'date-fns';
import dayjs, { type Dayjs } from 'dayjs';
import clsx from 'clsx';
import FullCalendar from '@fullcalendar/react';
import themePlugin from '@fullcalendar/react/themes/classic';
import dayGridPlugin from '@fullcalendar/react/daygrid';
import timeGridPlugin from '@fullcalendar/react/timegrid';
import interactionPlugin from '@fullcalendar/react/interaction';
import type {
  EventClickInfo,
  DatesSetInfo,
  DateClickInfo,
  DayCellInfo,
  MountInfo,
  ViewDisplayInfo,
  ButtonInfo,
  CalendarRef
} from '@fullcalendar/react';
import deLocale from '@fullcalendar/react/locales/de';
import bgLocale from '@fullcalendar/react/locales/bg';
import '@fullcalendar/react/skeleton.css';
import '@fullcalendar/react/themes/classic/theme.css';
import '@fullcalendar/react/themes/classic/palette.css';
import { useTranslation } from 'react-i18next';
import '@/assets/scss/appointments-calendar.scss';
import { ThemeContext } from '@/contexts/AntdTheme';
import {
  type Appointment,
  type CalendarExternalAppointment,
  type CalendarVisibilityMode
} from '@/api/mcp-appointments';
import { useOptionalAppointments } from '@/contexts/AppointmentContext';
import {
  getAppointmentDisplayExtension,
  parseAppointmentContext
} from '../extensions/extension-utils';
import {
  CALENDAR_STATUS_LEGEND_ITEMS,
  EXTERNAL_EVENT_COLORS,
  getServiceBookingMutedBackground,
  getStatusBackgroundColor,
  isChangeRequestDeadlinePassed,
  isServiceCalendarBooking,
  getCalendarAppointmentResourceLabel,
  getTimeGridAppointmentLayout,
  isShortTimeGridEvent,
  isTimeGridCalendarView,
  normalizeDetailsReference,
  toFullCalendarExternalEvent,
  type CalendarEventAudience
} from './calendarUtils';
import { ExternalCalendarEventDetailsPopup } from './ExternalCalendarEventDetailsPopup';
import {
  CalendarAppointmentEventContent,
  buildCalendarAppointmentEventContentProps
} from './CalendarAppointmentEventContent';
import { useClientCustomerDisplayMap } from '@/hooks/useClientCustomerDisplayMap';
import type { SupportedLanguage } from '@/i18n';

const { Text: AntText } = Typography;

/** Plus icon shown in month-view day cells to create an appointment on that date. */
const DAY_CELL_ADD_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M7 2h2v5h5v2H9v5H7V9H2V7h5V2z" />
  </svg>
);

/**
 * Builds CSS classes for an internal appointment event chip.
 *
 * Service bookings use muted cards with a status-colored left edge. Resource-only
 * bookings use dashed borders while still scheduled and solid borders once confirmed.
 */
function buildAppointmentEventClassName(
  appointment: Appointment,
  isServiceBooking: boolean,
  isUnconfirmed: boolean
): string {
  return clsx(
    'fc-event-calendar-appointment',
    isServiceBooking
      ? ['fc-event-service-booking', `fc-event-status-${appointment.status.toLowerCase()}`]
      : isUnconfirmed
        ? 'fc-event-unconfirmed'
        : 'fc-event-confirmed'
  );
}

/**
 * Locale definitions passed to FullCalendar.
 *
 * Keeping the array outside React rendering gives FullCalendar a stable option reference and
 * avoids unnecessary option churn.
 */
const fullCalendarLocales = [deLocale, bgLocale];

/**
 * Padded date range currently rendered by FullCalendar.
 *
 * In month view this can include previous-month and next-month filler cells. Parent data
 * loaders can use it to fetch everything visible in the grid.
 */
export interface CalendarVisibleRange {
  /** Inclusive start of the rendered range. */
  start: Date;
  /** Exclusive end of the rendered range. */
  end: Date;
}

/**
 * Inputs and callbacks used to render and interact with the appointment calendar.
 */
interface AppointmentsCalendarProps {
  /** Internal appointments rendered as clickable FullCalendar events. */
  appointments: Appointment[];
  /** Logical date selected by the parent manage view. */
  selectedDate: Dayjs;
  /** Called when calendar navigation or date picking changes the logical selected date. */
  onDateChange: (date: Dayjs) => void;
  /** Called after an internal appointment event is clicked. */
  onAppointmentClick: (appointment: Appointment, event: React.MouseEvent) => void;
  /**
   * Opens appointment details for the clicked event.
   * When set, used instead of manage {@link AppointmentProvider}.openDetails
   * so non-manage routes (e.g. /client) can own details UI.
   */
  onOpenDetails?: (appointment: Appointment, el: HTMLElement) => void;
  /** Enables month-cell create buttons and receives the clicked cell date. */
  onCreateAppointmentForDate?: (date: Dayjs) => void;
  /** Receives FullCalendar's rendered range for appointment/external-event loading. */
  onVisibleRangeChange?: (range: CalendarVisibleRange) => void;
  /** External calendar events rendered alongside appointments. */
  externalAppointments?: CalendarExternalAppointment[];
  /** Resource names used in external-calendar details. */
  resourceNameById?: Record<string, string>;
  /** Date keys (`YYYY-MM-DD`) that should display an external-conflict indicator. */
  conflictDays?: Set<string>;
  /** Whether external events and external-conflict indicators are shown. */
  showExternalCalendars?: boolean;
  /** Per-feed visibility mode; non-full visibility feeds do not open details. */
  visibilityByFeedId?: Record<string, CalendarVisibilityMode>;
  /** When true, shows a loading overlay over the calendar grid. */
  isLoading?: boolean;
  /**
   * Chip labeling mode. `customer` prefers resource/service names over guest/client labels
   * (used on /client). Defaults to staff/manage behavior.
   */
  audience?: CalendarEventAudience;
}


/**
 * Rebuilds an appointment from a FullCalendar event's custom data.
 *
 * FullCalendar stores non-standard event fields under `extendedProps`. We keep the fields flat
 * there so event click handlers and renderers can reconstruct the appointment without another
 * API lookup.
 */
const reconstructAppointmentFromExtendedProps = (extendedProps: any): Appointment => {
  return {
    id: extendedProps.appointmentId,
    clientId: extendedProps.clientId,
    companyId: extendedProps.companyId,
    resourceId: extendedProps.resourceId,
    serviceId: extendedProps.serviceId ?? null,
    startTime: extendedProps.startTime,
    endTime: extendedProps.endTime,
    status: extendedProps.status as Appointment['status'],
    format: (extendedProps.format as Appointment['format']) ?? 'IN_PERSON',
    meetingUrl: extendedProps.meetingUrl ?? null,
    meetingModeratorUrl: extendedProps.meetingModeratorUrl ?? null,
    userId: extendedProps.userId,
    sessionId: extendedProps.sessionId,
    anonymousName: extendedProps.anonymousName,
    anonymousPhone: extendedProps.anonymousPhone,
    anonymousEmail: extendedProps.anonymousEmail ?? null,
    notes: extendedProps.notes,
    metadata: extendedProps.metadata,
    context: extendedProps.context,
    createdAt: extendedProps.createdAt,
    updatedAt: extendedProps.updatedAt,
    service: extendedProps.service ?? null,
    allocatedResources: extendedProps.allocatedResources
  };
};

/**
 * Renders appointments and external calendar events in month, week, and day views.
 *
 * The component treats FullCalendar's logical current month (`view.currentStart`) separately
 * from the padded rendered grid range (`dateInfo.start` / `dateInfo.end`). This matters in
 * month view because FullCalendar displays outside-month filler days to complete the grid.
 */
export const AppointmentsCalendar: React.FC<AppointmentsCalendarProps> = ({
  appointments,
  selectedDate,
  onDateChange,
  onAppointmentClick,
  onOpenDetails,
  onCreateAppointmentForDate,
  onVisibleRangeChange,
  externalAppointments = [],
  resourceNameById = {},
  conflictDays,
  showExternalCalendars = false,
  visibilityByFeedId = {},
  isLoading = false,
  audience = 'staff'
}) => {
  const appointmentsContext = useOptionalAppointments();
  const { token } = theme.useToken();
  const { isDarkMode } = useContext(ThemeContext);
  const { t, i18n } = useTranslation('manage');
  /** Date currently shown by FullCalendar; may differ briefly from `selectedDate` while navigating. */
  const [currentDate, setCurrentDate] = useState<Date>(selectedDate.toDate());
  /** Active FullCalendar view type, e.g. `dayGridMonth` or `timeGridWeek`. */
  const [currentView, setCurrentView] = useState<string>('dayGridMonth');
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<CalendarRef | null>(null);
  /** External ICS event currently shown in the floating details popup, if any. */
  const [activeExternalEvent, setActiveExternalEvent] = useState<CalendarExternalAppointment | null>(null);
  const [externalDetailsOpen, setExternalDetailsOpen] = useState(false);
  const [externalDetailsAnchorKey, setExternalDetailsAnchorKey] = useState(0);

  /** Positions the external-event details popup relative to the clicked calendar chip. */
  const {
    refs: externalRefs,
    floatingStyles: externalFloatingStyles,
    isPositioned: externalDetailsPositioned,
    update: updateExternalFloating
  } = useFloating({
    open: externalDetailsOpen,
    onOpenChange: setExternalDetailsOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end', 'left', 'right']
      }),
      shift({ padding: 16 })
    ]
  });

  /** Recomputes popup position after the anchor element or open state changes. */
  useLayoutEffect(() => {
    if (!externalDetailsOpen) return;
    updateExternalFloating();
  }, [externalDetailsAnchorKey, externalDetailsOpen, updateExternalFloating]);

  /** Closes the external-event popup and clears its floating-ui anchor. */
  const closeExternalDetails = useCallback(() => {
    setExternalDetailsOpen(false);
    externalRefs.setReference(null);
    setActiveExternalEvent(null);
  }, [externalRefs]);

  /** Keeps FullCalendar aligned when the parent changes the selected date. */
  useEffect(() => {
    setCurrentDate(selectedDate.toDate());
  }, [selectedDate]);

  /**
   * Transforms appointment records into FullCalendar event objects.
   *
   * FullCalendar moves custom event fields into `extendedProps`. Every field needed by the
   * details popup, extension renderer, or click handlers is copied there explicitly.
   */
  const pendingClientLabel = t('appointmentComponents.calendar.appointment.clientPending');
  const appointmentFallbackLabel = t('appointmentComponents.calendar.appointment.fallbackTitle');
  /** Active UI language code used for appointment time formatting in event cards. */
  const language = (i18n.language?.split('-')[0] ?? 'en') as SupportedLanguage;
  const { userDisplayById } = useClientCustomerDisplayMap(audience === 'staff');
  const eventContentOptions = useMemo(
    () => ({ audience, appointmentFallbackLabel }),
    [audience, appointmentFallbackLabel]
  );

  const appointmentEvents = useMemo(() => {
    return appointments.map((appointment) => {
      const start = parseISO(appointment.startTime);
      const end = parseISO(appointment.endTime);
      const parsedContext = parseAppointmentContext(appointment as Appointment & { context?: any });
      const contextData = parsedContext?.data || {};
      const displayExtension = parsedContext
        ? getAppointmentDisplayExtension(appointment as Appointment & { context?: any })
        : undefined;
      const customStyle = displayExtension?.getEventStyle?.(appointment, contextData);
      const isServiceBooking = isServiceCalendarBooking(appointment);
      const statusColor = getStatusBackgroundColor(appointment.status);
      const isUnconfirmed = appointment.status === 'SCHEDULED';
      const isChangeRequestBlocked =
        audience === 'customer' && isChangeRequestDeadlinePassed(appointment.startTime);
      const cardProps = buildCalendarAppointmentEventContentProps(
        appointment,
        pendingClientLabel,
        language,
        userDisplayById,
        eventContentOptions
      );
      const customerTitle =
        getCalendarAppointmentResourceLabel(appointment) ||
        cardProps.headline ||
        appointmentFallbackLabel;

      const eventColor =
        customStyle?.backgroundColor ||
        customStyle?.borderColor ||
        (isServiceBooking ? getServiceBookingMutedBackground(appointment.status) : statusColor);
      const contrastColor =
        customStyle?.color || (isServiceBooking ? '#262626' : 'white');

      return {
        id: appointment.id,
        title: audience === 'customer' ? customerTitle : cardProps.headline || cardProps.clientLabel,
        start: start.toISOString(),
        end: end.toISOString(),
        color: eventColor,
        contrastColor,
        className: clsx(
          buildAppointmentEventClassName(appointment, isServiceBooking, isUnconfirmed),
          isChangeRequestBlocked && 'fc-event-change-request-blocked'
        ),
        extendedProps: {
          appointmentId: appointment.id,
          clientId: appointment.clientId,
          companyId: appointment.companyId,
          resourceId: appointment.resourceId,
          serviceId: appointment.serviceId ?? null,
          startTime: appointment.startTime,
          endTime: appointment.endTime,
          status: appointment.status,
          format: appointment.format ?? 'IN_PERSON',
          meetingUrl: appointment.meetingUrl ?? null,
          meetingModeratorUrl: appointment.meetingModeratorUrl ?? null,
          userId: appointment.userId,
          sessionId: appointment.sessionId,
          anonymousName: appointment.anonymousName,
          anonymousPhone: appointment.anonymousPhone,
          anonymousEmail: appointment.anonymousEmail,
          notes: appointment.notes,
          metadata: appointment.metadata,
          createdAt: appointment.createdAt,
          updatedAt: appointment.updatedAt,
          service: appointment.service ? JSON.parse(JSON.stringify(appointment.service)) : null,
          allocatedResources: appointment.allocatedResources
            ? JSON.parse(JSON.stringify(appointment.allocatedResources))
            : [],
          headline: cardProps.headline,
          context: appointment.context ? JSON.parse(JSON.stringify(appointment.context)) : null,
          contextData: contextData ? JSON.parse(JSON.stringify(contextData)) : {},
          contextSource: parsedContext?.source || null,
          clientName: cardProps.clientLabel
        }
      };
    });
  }, [
    appointments,
    language,
    pendingClientLabel,
    userDisplayById,
    eventContentOptions,
    audience,
    appointmentFallbackLabel
  ]);

  /**
   * Combines first-party appointments with optional external calendar events.
   *
   * External events are converted through `toFullCalendarExternalEvent` so their visibility
   * mode and resource labels are available to event click handling.
   */
  const calendarEvents = useMemo(() => {
    if (!showExternalCalendars || externalAppointments.length === 0) {
      return appointmentEvents;
    }

    const externalEvents = externalAppointments.map((external) => {
      const resourceName =
        resourceNameById[external.resourceId] ||
        t('appointmentComponents.resourceFallback', { id: external.resourceId });
      const visibilityMode =
        visibilityByFeedId[external.calendarFeedId] ?? 'FULL_VISIBILITY';
      return toFullCalendarExternalEvent(external, resourceName, t, visibilityMode);
    });

    return [...appointmentEvents, ...externalEvents];
  }, [appointmentEvents, externalAppointments, resourceNameById, showExternalCalendars, t, visibilityByFeedId]);

  /**
   * Opens the appropriate details UI for the clicked calendar event.
   *
   * Internal appointments are reconstructed from FullCalendar `extendedProps`. External events
   * use a separate floating popup and respect feed visibility settings before opening details.
   */
  const handleEventClick = useCallback((clickInfo: EventClickInfo) => {
    const extendedProps = clickInfo.event.extendedProps;

    if (extendedProps?.eventKind === 'external') {
      const visibilityMode =
        (extendedProps.visibilityMode as CalendarVisibilityMode | undefined) ??
        visibilityByFeedId[extendedProps.calendarFeedId as string] ??
        'FULL_VISIBILITY';

      if (visibilityMode !== 'FULL_VISIBILITY') {
        return;
      }

      const external = externalAppointments.find((e) => e.id === extendedProps.externalId);
      if (!external) return;

      const reference = normalizeDetailsReference(clickInfo.el);
      if (reference) {
        externalRefs.setReference(reference);
      }
      setExternalDetailsAnchorKey((key) => key + 1);
      setActiveExternalEvent(external);
      setExternalDetailsOpen(true);
      return;
    }

    const appointment = reconstructAppointmentFromExtendedProps(extendedProps);

    if (onOpenDetails) {
      onOpenDetails(appointment, clickInfo.el);
    } else {
      appointmentsContext?.openDetails(appointment, clickInfo.el);
    }

    const syntheticEvent = new MouseEvent('click', { bubbles: true, cancelable: true }) as unknown as React.MouseEvent;
    onAppointmentClick(appointment, syntheticEvent);
  }, [
    appointmentsContext,
    externalAppointments,
    externalRefs,
    onAppointmentClick,
    onOpenDetails,
    visibilityByFeedId
  ]);

  /** Updates the selected date when the user clicks a day cell. */
  const handleDateClick = useCallback((clickInfo: DateClickInfo) => {
    const selectedDayjs = dayjs(clickInfo.date);
    setCurrentDate(clickInfo.date);
    onDateChange(selectedDayjs);
  }, [onDateChange]);

  /** Formats a FullCalendar cell date to the date-key format used by conflict lookups. */
  const getDayCellDateKey = useCallback((date: Date) => dayjs(date).format('YYYY-MM-DD'), []);

  /**
   * Runs whenever the user navigates the calendar or switches views.
   *
   * Reports the padded visible grid range to the parent for data loading, keeps local
   * calendar state in sync, and updates the parent selected date when the logical month
   * or week changes.
   */
  const handleDatesSet = useCallback((dateInfo: DatesSetInfo) => {
    const logicalViewStart = dateInfo.view.currentStart ?? dateInfo.start;
    const viewDate = dayjs(logicalViewStart);

    onVisibleRangeChange?.({ start: dateInfo.start, end: dateInfo.end });

    setCurrentDate((prev) => {
      if (isSameDay(prev, logicalViewStart)) return prev;
      return logicalViewStart;
    });

    if (!isSameDay(viewDate.toDate(), selectedDate.toDate())) {
      onDateChange(viewDate);
    }
  }, [selectedDate, onDateChange, onVisibleRangeChange]);

  /** Tracks the active FullCalendar view so the day-view date picker appears only in day view. */
  const handleViewChange = useCallback((mountArg: MountInfo<ViewDisplayInfo>) => {
    const viewType = mountArg.view.type;
    if (viewType !== currentView) {
      setCurrentView(viewType);
    }
  }, [currentView]);

  /** Returns whether the given day should show an external-calendar conflict badge. */
  const getDayCellConflict = useCallback((date: Date) => {
    const dateKey = getDayCellDateKey(date);
    return showExternalCalendars && (conflictDays?.has(dateKey) ?? false);
  }, [conflictDays, getDayCellDateKey, showExternalCalendars]);

  /**
   * Adds layout and state classes to each day cell.
   *
   * Month-only affordances such as minimum height and conflict styling are skipped
   * inside the "+N more" popover so it stays compact.
   */
  const dayCellClass = useCallback((info: DayCellInfo) => {
    const hasConflict = getDayCellConflict(info.date);
    return clsx(
      'appt-day-cell',
      info.inPopover && 'appt-day-cell--popover',
      currentView === 'dayGridMonth' && !info.inPopover && 'appt-day-cell--month',
      info.isToday && !info.inPopover && 'appt-day-cell--today',
      info.isOther && !info.inPopover && 'appt-day-cell--other',
      hasConflict && !info.inPopover && 'appt-day-cell--conflict'
    );
  }, [currentView, getDayCellConflict]);

  /** Distinguishes the main grid event stack from the scrollable "+N more" popover list. */
  const dayCellInnerClass = useCallback((info: DayCellInfo) => {
    return clsx('appt-day-events', info.inPopover && 'appt-day-events--popover');
  }, []);

  /**
   * Renders the top row of a month cell: optional create button, conflict badge, and day number.
   */
  const renderDayCellTopContent = useCallback((info: DayCellInfo) => {
    const cellDate = dayjs(info.date);
    const hasConflict = getDayCellConflict(info.date);
    const showAddButton =
      currentView === 'dayGridMonth'
      && !info.isOther
      && !info.inPopover
      && !!onCreateAppointmentForDate
      && !cellDate.endOf('day').isBefore(dayjs().startOf('day'));

    return (
      <>
        {showAddButton || hasConflict ? (
          <div className="appt-day-top-left">
            {showAddButton ? (
              <button
                type="button"
                className="appt-day-add-btn"
                aria-label={t('appointmentComponents.addAppointmentOnDay')}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onCreateAppointmentForDate(cellDate);
                }}>
                {DAY_CELL_ADD_ICON}
              </button>
            ) : null}
            {hasConflict ? (
              <span
                className="appt-day-conflict-badge"
                title={t('appointmentComponents.conflictDayBadge')}>
                {t('appointmentComponents.conflictDayBadge')}
              </span>
            ) : null}
          </div>
        ) : null}
        <span className="appt-day-number">{info.dayNumberText}</span>
      </>
    );
  }, [currentView, getDayCellConflict, onCreateAppointmentForDate, t]);

  /** Maps FullCalendar toolbar buttons to Ant Design–aligned calendar button classes. */
  const buttonClass = useCallback((info: ButtonInfo) => {
    return clsx(
      'appt-cal-btn',
      info.isSelected && 'appt-cal-btn--active',
      info.name === 'today' && 'appt-cal-btn--today'
    );
  }, []);

  /** Adds a view-specific class so SCSS can target month, week, and day layouts separately. */
  const viewClass = useCallback((info: ViewDisplayInfo) => {
    return clsx('appt-cal-view', `appt-cal-view--${info.view.type.toLowerCase()}`);
  }, []);

  /** Compact renderer for imported ICS blocked-time events. */
  const renderExternalEventContent = useCallback((eventInfo: any) => {
    const label = eventInfo.event.extendedProps?.label ?? eventInfo.event.title;
    const startTime = eventInfo.event.extendedProps?.startTime ?? eventInfo.event.start?.toISOString?.();
    const endTime = eventInfo.event.extendedProps?.endTime ?? eventInfo.event.end?.toISOString?.();
    const compact =
      isTimeGridCalendarView(eventInfo.view.type)
      && startTime
      && endTime
      && isShortTimeGridEvent(startTime, endTime);

    return (
      <div
        className={clsx('fc-event-external-content', compact && 'fc-event-external-content--compact')}
        title={label}>
        <span className="fc-event-external-content__time">{eventInfo.timeText}</span>
        <span className="fc-event-external-content__label">{label}</span>
      </div>
    );
  }, []);

  /**
   * Renders event content inside FullCalendar.
   *
   * External events use a compact blocked-time renderer. Internal appointments go through
   * the display-extension hook first, then fall back to the shared appointment card.
   */
  const renderEventContent = useCallback(
    (eventInfo: any) => {
      const extendedProps = eventInfo.event.extendedProps;
      if (!extendedProps) return null;

      if (extendedProps.eventKind === 'external') {
        return renderExternalEventContent(eventInfo);
      }

      const appointment = reconstructAppointmentFromExtendedProps(extendedProps);
      const contextData = extendedProps.contextData || {};
      const contextSource = extendedProps.contextSource;
      const storedContext = extendedProps.context;
      const timeGridLayout = isTimeGridCalendarView(eventInfo.view.type)
        ? getTimeGridAppointmentLayout(
          appointment,
          eventInfo.event.start,
          eventInfo.event.end
        )
        : 'detailed';

      let parsedContext: ReturnType<typeof parseAppointmentContext> = null;
      if (contextSource && Object.keys(contextData).length > 0) {
        parsedContext = { source: contextSource, data: contextData };
      } else if (storedContext) {
        parsedContext = parseAppointmentContext(appointment as Appointment & { context?: any });
      }

      const displayExtension = parsedContext
        ? getAppointmentDisplayExtension(appointment as Appointment & { context?: any })
        : undefined;

      const cardContent = (
        <CalendarAppointmentEventContent
          {...buildCalendarAppointmentEventContentProps(
            appointment,
            pendingClientLabel,
            language,
            userDisplayById,
            eventContentOptions
          )}
          layout={timeGridLayout}
        />
      );

      if (displayExtension?.renderEventContent) {
        const customRender = displayExtension.renderEventContent(
          appointment,
          contextData,
          () => cardContent
        );
        if (customRender) {
          return customRender;
        }
      }

      return cardContent;
    },
    [
      language,
      pendingClientLabel,
      renderExternalEventContent,
      userDisplayById,
      eventContentOptions
    ]
  );

  /** Lets users jump directly to a date while they are in the day view. */
  const handleDatePickerChange = useCallback((date: dayjs.Dayjs | null) => {
    if (date && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(date.toDate());
      setCurrentDate(date.toDate());
      onDateChange(date);
    }
  }, [onDateChange]);

  /** Human-readable resource label for the currently open external-event popup. */
  const activeExternalResourceName = activeExternalEvent
    ? resourceNameById[activeExternalEvent.resourceId] ??
      t('appointmentComponents.resourceFallback', { id: activeExternalEvent.resourceId })
    : '';

  return (
    <>
      {activeExternalEvent && (
        <>
          <ExternalCalendarEventDetailsPopup
            event={activeExternalEvent}
            resourceName={activeExternalResourceName}
            visible={externalDetailsOpen}
            isPositioned={externalDetailsPositioned}
            onClose={closeExternalDetails}
            floatingStyles={externalFloatingStyles}
            floatingRef={externalRefs.setFloating}
          />
          {externalDetailsOpen ? (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 999
              }}
              onClick={closeExternalDetails}
            />
          ) : null}
        </>
      )}

      <Card
        style={{
          borderRadius: '8px',
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadowTertiary,
          overflow: 'hidden'
        }}
        styles={{ body: { padding: '24px' } }}>
        <div className="appointments-calendar" ref={calendarContainerRef} style={{ position: 'relative' }}>
          {isLoading ? (
            <div className="appointments-calendar-loading-overlay" aria-busy="true">
              <Spin size="large" />
            </div>
          ) : null}
          {/* DatePicker for day view - allows jumping to a specific date */}
          {currentView === 'timeGridDay' && (
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
              <AntText type="secondary" style={{ fontSize: '14px' }}>{t('appointmentComponents.jumpToDate')}</AntText>
              <DatePicker
                value={dayjs(currentDate)}
                onChange={handleDatePickerChange}
                format="YYYY-MM-DD"
                style={{ width: 150 }}
                allowClear={false}
              />
            </div>
          )}
          <FullCalendar
            ref={calendarRef}
            className="appt-cal-root"
            colorScheme={isDarkMode ? 'dark' : 'light'}
            plugins={[themePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin]}
            locales={fullCalendarLocales}
            locale={i18n.language}
            firstDay={1}
            initialView={currentView}
            initialDate={currentDate}
            events={calendarEvents}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            datesSet={handleDatesSet}
            viewDidMount={handleViewChange}
            viewClass={viewClass}
            dayCellClass={dayCellClass}
            dayCellTopClass="appt-day-top"
            dayCellTopInnerClass="appt-day-top-inner"
            dayCellTopContent={renderDayCellTopContent}
            dayCellInnerClass={dayCellInnerClass}
            dayHeaderClass="appt-cal-day-header"
            toolbarClass="appt-cal-toolbar"
            toolbarSectionClass="appt-cal-toolbar-section"
            toolbarTitleClass="appt-cal-toolbar-title"
            buttonGroupClass="appt-cal-btn-group"
            buttonClass={buttonClass}
            eventClass="appt-cal-event"
            moreLinkClass="appt-cal-more-link"
            popoverClass="appt-cal-popover"
            eventContent={renderEventContent}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            height="auto"
            eventDisplay="block"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            dayMaxEvents={3}
            moreLinkClick="popover"
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            allDaySlot={false}
            eventShortHeight={52}
            eventMinHeight={18}
          />

        </div>
      </Card>

      <Card
        className="calendar-status-legend-card"
        style={{
          marginTop: '20px',
          borderRadius: '8px',
          border: `1px solid ${token.colorBorderSecondary}`,
          backgroundColor: token.colorBgContainer
        }}
        styles={{ body: { padding: '16px 20px' } }}>
        <div className="calendar-status-legend">
          <AntText type="secondary" className="calendar-status-legend__title">
            {t('appointmentComponents.legend.title')}
          </AntText>
          <Space size="middle" wrap className="calendar-status-legend__items">
            {CALENDAR_STATUS_LEGEND_ITEMS.map((item) => {
              const color = getStatusBackgroundColor(item.status);
              return (
                <div key={item.status} className="calendar-status-legend__item">
                  <span
                    className="calendar-status-legend__swatch calendar-status-legend__swatch--status"
                    style={{
                      backgroundColor: color,
                      borderColor: color,
                      borderStyle: item.dashed ? 'dashed' : 'solid',
                      borderWidth: item.dashed ? 2 : 1
                    }}
                  />
                  <AntText style={{ fontSize: '13px', fontWeight: 500 }}>{t(item.labelKey)}</AntText>
                </div>
              );
            })}
            {showExternalCalendars && (
              <div className="calendar-status-legend__item">
                <ExternalLegendSwatch />
                <AntText style={{ fontSize: '13px', fontWeight: 500 }}>
                  {t('appointmentComponents.legend.externalBlocked')}
                </AntText>
              </div>
            )}
          </Space>
        </div>
      </Card>
    </>
  );
};

/** Legend swatch matching imported external calendar blocked-time events. */
function ExternalLegendSwatch() {
  return (
    <div
      style={{
        width: '14px',
        height: '14px',
        backgroundColor: EXTERNAL_EVENT_COLORS.backgroundColor,
        borderRadius: '4px',
        borderLeft: `3px solid ${EXTERNAL_EVENT_COLORS.borderColor}`,
        flexShrink: 0,
        marginTop: '1px'
      }}
    />
  );
}
